import { formatString } from './formatString';
import {
  CheckboxSelectControl,
  FilterError,
  FilterType,
  IntegerInputControl,
  MatchMode,
  RealInputControl,
  StringInputControl,
  TextAreaControl,
} from '../types/types';
import { TranslateFn } from '../hooks/context/useTranslate';
import { isEqual } from 'lodash';
import {
  DEFAULT_CURRENCY_PLACEHOLDER,
  START_OF_DAY_TIME_VALUES,
  TIME_FILTER_DATE_STRING,
} from '../constants';

import { getGridFormattedFloat } from './getGridFormattedFloat';
import { getFormattedFloat } from './getFormattedFloat';
import { debugLogger } from '../debug/DebugLogger';

const log = debugLogger.scope('formatting');

const logInvalidDate = (
  date: Date,
  column: { name?: string; type?: string },
  value: unknown
) => {
  // isEnabled first: this runs per date cell per render — with the feature
  // off it must cost a single flag read, not a getTime/isNaN per cell.
  if (!debugLogger.isEnabled('formatting') || !isNaN(date.getTime())) {
    return;
  }

  /** @debugFeature formatting Logs a date/time/dateTime column value that failed to parse and renders as "Invalid Date". */
  log('invalid date value', () => ({
    columnName: column.name,
    columnType: column.type,
    value,
  }));
};

export const formatValue = (
  rowdata: any,
  column: any,
  isDetail: boolean = false
) => {
  const value = rowdata[column.name];

  if (value === null || value === undefined) {
    return '-';
  }

  const valueLabels = column.valueLabels as Record<string, string> | undefined;
  const mapValue = (item: unknown): string => {
    const key = String(item);
    if (
      valueLabels &&
      Object.prototype.hasOwnProperty.call(valueLabels, key)
    ) {
      return valueLabels[key];
    }
    return valueLabels && column.unknownValueLabel
      ? column.unknownValueLabel
      : key;
  };

  if (Array.isArray(value) && valueLabels) {
    return value.map(mapValue).join(', ');
  }

  if (typeof value === 'object') {
    return isDetail ? JSON.stringify(value, null, 2) : JSON.stringify(value);
  }

  if (valueLabels) {
    return mapValue(value);
  }

  if (typeof value === 'boolean') {
    if (value === true) {
      return 'Ano';
    } else {
      return 'Ne';
    }
  }

  if (column.type === 'date') {
    const date = new Date(value);
    logInvalidDate(date, column, value);
    return date.toLocaleDateString();
  } else if (column.type === 'time') {
    const date = new Date(`${TIME_FILTER_DATE_STRING}${value}`);
    logInvalidDate(date, column, value);
    return date.toLocaleTimeString();
  } else if (column.type === 'dateTime') {
    const date = new Date(value);
    logInvalidDate(date, column, value);
    return date.toLocaleString();
  }

  // sprintf('%.2f', '') yields the string "NaN", so blank values skip the template
  if (column.format && String(value).trim() !== '') {
    const formatted = formatString(column.format, value);

    // Rewrite formatting in case of float column
    if (column.type === 'float') {
      const formattedFloat = getGridFormattedFloat(formatted, column);
      return formattedFloat || formatted;
    }

    /** @debugFeature formatting Logs a non-float column whose `format` result is computed but discarded — the value falls through to the remaining formatting branches. */
    log('non-float column format result discarded', () => ({
      columnName: column.name,
      columnType: column.type,
      format: column.format,
      discardedResult: formatted,
    }));
  }

  if (column.formatItem && rowdata[column.formatItem]) {
    const formatted = formatString(rowdata[column.formatItem], value);

    // Rewrite formatting in case of float column
    if (column.type === 'float') {
      const formattedFloat = getGridFormattedFloat(formatted, column);
      return formattedFloat || formatted;
    }

    return formatted;
  }

  if (column.type === 'float') {
    return getGridFormattedFloat(value, column);
  }

  if (typeof value === 'number') {
    return getFormattedFloat(String(value));
  }

  return value.toString();
};

export const getMultisortFromDataset = (dataset) => {
  if (!dataset?.sort) {
    return null;
  }
  return dataset.sort.map((sortItem) => ({
    field: sortItem.item,
    order: sortItem.dir === 'desc' ? -1 : 1,
  }));
};

export const replaceCurrencyInDataset = (dataset, currencyFormat) => {
  // Debug bookkeeping is collected only with the feature on — the loop runs
  // over every dataset item regardless.
  const collectDebug = debugLogger.isEnabled('formatting');
  const replacedItemNames: string[] = [];

  dataset.items.forEach((item) => {
    if (item.format?.includes(DEFAULT_CURRENCY_PLACEHOLDER)) {
      const formattingString = item.format.replaceAll(
        DEFAULT_CURRENCY_PLACEHOLDER,
        currencyFormat
      );
      item.format = formattingString;
      if (collectDebug) {
        replacedItemNames.push(item.name);
      }
    }
  });

  if (replacedItemNames.length > 0) {
    /** @debugFeature formatting Logs dataset items whose `format` had the currency placeholder replaced, and the currency format used; silent when nothing was replaced. */
    log('currency placeholder replaced in dataset formats', () => ({
      datasetId: dataset.datasetId,
      itemNames: replacedItemNames,
      currencyFormat,
    }));
  }

  return dataset;
};

export const replaceCurrencyInData = (dataset, data, currencyFormat) => {
  // Debug bookkeeping is collected only with the feature on — the loop runs
  // over every row regardless.
  const collectDebug = debugLogger.isEnabled('formatting');
  const replacedRowCounts: Record<string, number> = {};

  dataset.items.forEach((column) => {
    if (column.formatItem) {
      data.forEach((row) => {
        if (row[column.formatItem]?.includes(DEFAULT_CURRENCY_PLACEHOLDER)) {
          const formattingString = row[column.formatItem].replaceAll(
            DEFAULT_CURRENCY_PLACEHOLDER,
            currencyFormat
          );
          row[column.formatItem] = formattingString;
          if (collectDebug) {
            replacedRowCounts[column.formatItem] =
              (replacedRowCounts[column.formatItem] ?? 0) + 1;
          }
        }
      });
    }
  });

  if (Object.keys(replacedRowCounts).length > 0) {
    /** @debugFeature formatting Logs per formatItem how many rows had the currency placeholder replaced, and the currency format used; silent when nothing was replaced. */
    log('currency placeholder replaced in row data', () => ({
      datasetId: dataset.datasetId,
      replacedRowCounts,
      currencyFormat,
    }));
  }

  return data;
};

export const scrollGridToTop = (gridRef) => {
  if (gridRef.current) {
    const tableWrapper = gridRef.current.querySelector('.p-virtualscroller');
    if (tableWrapper && tableWrapper.scrollTop !== 0) {
      // Scrolling to 0 breaks virtualScroller, need to scroll to 1px from the top
      tableWrapper.scrollTo({ top: 1 });
    }
  }
};

export const getDefaultMatchModeByType = (
  dataType: FilterType | undefined,
  controlType?: string
): MatchMode => {
  if (controlType === 'multiCheckbox') {
    return 'in';
  }
  switch (dataType) {
    case 'string':
      return 'contains';
    case 'date':
      return 'dateIs';
    case 'dateTime':
      return 'datetime-equals';
    default:
      return 'equals';
  }
};

export const getDefaultNumericFilterValue = (
  control: IntegerInputControl | RealInputControl | undefined
) => {
  let value = 0;
  const max = control?.max;
  const min = control?.min;
  if (max !== undefined && max < 0) {
    value = max;
  }
  if (min !== undefined && min > 0) {
    value = min;
  }
  return value.toString();
};

export const getDefaultFilterValue = (
  filterItem: any
): string | boolean | null => {
  if (
    filterItem.default &&
    Object.prototype.hasOwnProperty.call(filterItem.default, 'value')
  ) {
    const newValue = filterItem.default.value;
    return typeof newValue === 'boolean' || typeof newValue === 'string'
      ? newValue
      : newValue.toString();
  }
  if (filterItem.control?.type === 'combobox') {
    return null;
  } else if (['float', 'integer'].includes(filterItem.type)) {
    return getDefaultNumericFilterValue(filterItem.control);
  } else if (
    filterItem.type === 'boolean' ||
    filterItem.control?.type === 'checkbox'
  ) {
    return (filterItem.control as CheckboxSelectControl)?.inverse
      ? true
      : false;
  }
  return '';
};

export const isEmptyValue = (
  value: string | number | unknown[] | null | boolean | undefined | unknown
): boolean =>
  value === '' ||
  (Array.isArray(value) && value.length === 0) ||
  value === null ||
  value === undefined;

export const getIsBetweenMatchMode = (matchMode: MatchMode) =>
  ['between', 'dateBetween', 'datetime-between'].includes(matchMode);

export const getColumnByFilterParam = (filterParam: string, dataset: any) =>
  dataset?.items.find(
    (column) => column.filterParam && column.filterParam === filterParam
  );

export const updateActiveSortingFromLazyState = (
  oldActiveSorting,
  lazyState
) => {
  const newSorting = [...lazyState.multiSortMeta];
  return isEqual(newSorting, oldActiveSorting) ? oldActiveSorting : newSorting;
};

export const getMinMaxDates = (
  minValue: Date | null,
  maxValue: Date | null,
  allowTime: boolean
) => {
  let min: Date | null = null;
  let max: Date | null = null;
  if (minValue) {
    min = new Date(minValue.getTime());
    if (!allowTime) {
      min.setHours(...START_OF_DAY_TIME_VALUES);
    }
  }
  if (maxValue) {
    max = new Date(maxValue.getTime());
    if (!allowTime) {
      max.setHours(...START_OF_DAY_TIME_VALUES);
    }
  }
  return { min, max };
};

export const validateTextInput = (
  value: string,
  control: TextAreaControl | StringInputControl | undefined,
  t: TranslateFn
) => {
  const maxLen = control?.maxLen;
  const minLen = control?.minLen;
  if (maxLen && value.length > maxLen) {
    return [t('errors.maxLength').replace('{0}', maxLen.toString())];
  }
  if (minLen && value.length < minLen) {
    return [t('errors.minLength').replace('{0}', minLen.toString())];
  }
  const regExp = control?.regExp;
  if (regExp) {
    const regExpError: FilterError = [];
    regExp.forEach(({ exp, msg }) => {
      const expression = new RegExp(exp);
      if (!expression.test(value)) {
        regExpError.push(msg);
      }
    });
    if (regExpError.length) {
      return regExpError;
    }
  }
  return null;
};

export const validateNumericInput = (
  value,
  control: IntegerInputControl | RealInputControl | undefined,
  t: TranslateFn
) => {
  const number = parseFloat(value);
  const isValidNumber =
    !isNaN(number) &&
    value?.split('.').length < 3 &&
    !value.startsWith('.') &&
    !value.endsWith('.');
  if (isValidNumber) {
    const max = control?.max;
    const min = control?.min;
    if (max && number > max) {
      return [t('errors.maxValue').replace('{0}', max.toString())];
    }
    if (min && number < min) {
      return [t('errors.minValue').replace('{0}', min.toString())];
    }
    return null;
  }
  return [t('errors.invalidNumber')];
};
